import React, { FunctionComponent, ReactNode, useCallback } from 'react'
import classNames from 'classnames'
import { Button, Icon } from '@lightspeed/design-system-react'
import {
  QuickKeyBlank,
  QuickKeyFolder,
  QuickKeyKey,
  QuickKeyRoot,
} from '../utils/qk-types'
import { QuickKeyNodeType } from '../utils/qk.constants'
import { HandleDropParams } from '../quickKeyUtils'
import { QKNode } from './QKNode'

interface QKRootProps {
  quickKey: QuickKeyRoot | QuickKeyFolder
  focussed: QuickKeyFolder | QuickKeyKey | QuickKeyBlank | null
  selectedBlank?: QuickKeyBlank | null
  setRoot: (node: QuickKeyFolder | null) => void
  setFocus: (node: QuickKeyFolder | QuickKeyKey | QuickKeyBlank) => void
  selectQuickKey: (node: QuickKeyFolder | QuickKeyKey | QuickKeyBlank) => void
  editFolder?: (folder: QuickKeyFolder) => void
  handleDrop?: (params: HandleDropParams) => void
}

interface FolderWrapperProps {
  quickKey: QuickKeyFolder
  setRoot: (node: QuickKeyFolder | null) => void
  setFocus: (node: QuickKeyFolder | QuickKeyKey) => void
  editFolder?: (folder: QuickKeyFolder) => void
  children: ReactNode
}

const FolderWrapper: FunctionComponent<FolderWrapperProps> = ({
  quickKey,
  setRoot,
  setFocus,
  editFolder,
  children,
}) => {
  const close = useCallback(() => {
    setRoot(null)
    setFocus(quickKey)
  }, [quickKey, setFocus, setRoot])
  return (
    <div
      className={classNames(
        'vd-quick-key-folder-view',
        `vd-quick-key-folder-view--${quickKey.viewData.color}`
      )}
    >
      <div className="vd-quick-key-folder-header">
        {editFolder ? (
          <Button
            onClick={() => editFolder(quickKey)}
            className="vd-quick-key-folder-action vd-quick-key-folder-action--edit vd-pa0"
          >
            <Icon icon="pencil" />
          </Button>
        ) : null}
        <Button
          onClick={close}
          className="vd-quick-key-folder-action vd-quick-key-folder-action--close vd-pa0"
          data-testid="quick-key-folder-close"
          data-track="quick-key-folder-close"
        >
          <Icon icon="close" />
        </Button>
        {quickKey.viewData.label}
      </div>
      {children}
    </div>
  )
}

const Grid: FunctionComponent<QKRootProps> = ({
  quickKey,
  focussed,
  selectedBlank,
  setFocus,
  selectQuickKey,
  editFolder,
  handleDrop,
}) => {
  return (
    <div className="vd-quick-key-grid">
      {quickKey.children.map(page => (
        <div className="vd-quick-key-page" key={page.node.id}>
          {'pageColumns' in page.viewData
            ? page.viewData.pageColumns.map((column, i) => (
                <div className="vd-quick-key-page-column" key={i}>
                  {column.map(quickKey => (
                    <QKNode
                      key={quickKey.node.id}
                      quickKey={quickKey}
                      isSelected={selectedBlank?.node.id === quickKey.node.id}
                      isFocussed={focussed === quickKey}
                      setFocus={setFocus}
                      selectQuickKey={selectQuickKey}
                      editFolder={editFolder}
                      handleDrop={handleDrop}
                    />
                  ))}
                </div>
              ))
            : page.node.id}
        </div>
      ))}
    </div>
  )
}

export const QKRoot: FunctionComponent<QKRootProps> = ({
  quickKey,
  focussed,
  selectedBlank,
  setRoot,
  setFocus,
  selectQuickKey,
  editFolder,
  handleDrop,
}) => {
  switch (quickKey.type) {
    case QuickKeyNodeType.ROOT:
      return (
        <Grid
          quickKey={quickKey}
          focussed={focussed}
          selectedBlank={selectedBlank}
          setRoot={setRoot}
          setFocus={setFocus}
          selectQuickKey={selectQuickKey}
          handleDrop={handleDrop}
        />
      )
    case QuickKeyNodeType.FOLDER:
      return (
        <FolderWrapper
          quickKey={quickKey}
          setRoot={setRoot}
          setFocus={setFocus}
          editFolder={editFolder}
        >
          <Grid
            quickKey={quickKey}
            focussed={focussed}
            selectedBlank={selectedBlank}
            setRoot={setRoot}
            setFocus={setFocus}
            selectQuickKey={selectQuickKey}
            handleDrop={handleDrop}
          />
        </FolderWrapper>
      )
    default:
      return null
  }
}
